import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import isFunction from 'lodash/isFunction';

import {
  PrevNavigationWithHandlers,
  NextNavigationWithHandlers,
} from '@Pages/RetroSynthesis/components/RsSlidable/RsSlidableNavigationBothSides';
import RsSlidableNavigationRightSide from '@Pages/RetroSynthesis/components/RsSlidable/RsSlidableNavigationRightSide';

import './RsSlidable.custom.scss';

export const CONTROLS_PLACEMENTS = {
  rightSide: 'rightSide',
  bothSides: 'bothSides',
};

function RsSlidable({
  className,
  containerClassName,
  contentClassName,
  contentAttrs,
  itemClassName,
  itemAttrs,
  items = [],
  renderItem,
  visibleItemIndex,
  resized,
  isSmile = false,
  centerItemsIfNavigationControlsNotVisible = false,
  controlsPlacement = CONTROLS_PLACEMENTS.rightSide,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const itemsRef = useRef([]);

  const [isOverflown, setIsOverflown] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [contentOffset, setContentOffset] = useState(0);

  const updateIsOverflown = () => {
    const containerWidth = containerRef.current?.offsetWidth;
    const contentWidth = contentRef.current?.offsetWidth;
    setIsOverflown(contentWidth > containerWidth);
  };

  const updateContentOffset = () => {
    if (!isOverflown) {
      setContentOffset(0);
    }
  };

  const updateDisabledNavigation = () => {
    const containerWidth = containerRef.current?.offsetWidth;
    const contentWidth = contentRef.current?.offsetWidth;
    setPrevDisabled(contentOffset >= 0);
    setNextDisabled(contentOffset <= containerWidth - contentWidth);
  };

  const resizeCallback = () => {
    updateIsOverflown();
    updateDisabledNavigation();
  };

  // Remove excess itemsRef when items length change
  useEffect(() => {
    if (itemsRef.current) {
      itemsRef.current = itemsRef.current.slice(0, items.length);
    }
  }, [items.length]);

  // resizeCallback on window resize
  useEffect(() => {
    window.addEventListener('resize', resizeCallback);
    return () => {
      window.removeEventListener('resize', resizeCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // resizeCallback on items or resized props change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resizeCallback, [items, resized]);

  // updateDisabledNavigation on contentOffset prop change
  useEffect(updateDisabledNavigation, [contentOffset]);

  // reset offset if items fit container
  useEffect(updateContentOffset, [isOverflown]);

  // Restrict left and right edge cases and call setOffset
  const applyContentOffset = (offset) => {
    const containerWidth = containerRef.current?.offsetWidth;
    const contentWidth = contentRef.current?.offsetWidth;
    let adjustedOffset = offset;

    if (offset > 0) {
      // restrain left
      adjustedOffset = 0;
    } else if (offset < containerWidth - contentWidth) {
      // restrain right
      adjustedOffset = containerWidth - contentWidth;
    }

    setContentOffset(adjustedOffset);
  };

  const navigationProps = {
    containerElement: containerRef.current,
    contentOffset,
    applyContentOffset,
    prevDisabled,
    nextDisabled,
    itemsElements: itemsRef.current,
    visibleItemIndex,
  };

  return (
    <div className={cn('rs-slidable', className)}>
      {isOverflown && controlsPlacement === CONTROLS_PLACEMENTS.bothSides && <PrevNavigationWithHandlers {...navigationProps} />}
      <div
        ref={containerRef}
        className={cn(
          'rs-slidable__container c-els-tab-group',
          { 'rs-slidable__container-centered': !isOverflown && centerItemsIfNavigationControlsNotVisible },
          containerClassName
        )}>
        <ul
          ref={contentRef}
          className={cn('rs-slidable__content', contentClassName)}
          {...contentAttrs}
          style={{ left: `${contentOffset}px` }}>
          {items.map((item, i) => {
            const itemCN = isFunction(itemClassName) ? itemClassName(item, i) : itemClassName;
            const attrs = isFunction(itemAttrs) ? itemAttrs(item, i) : itemAttrs;
            const isActive = visibleItemIndex === i;
            return (
              <li
                key={`rs-slidable-item-${i}`}
                ref={(el) => {
                  itemsRef.current[i] = el;
                }}
                className={cn(
                  'rs-slidable__content-item',
                  { 'c-els-tab-group__text c-els-tab-group__item': !isSmile },
                  { 'c-els-tab-group__item--active': isActive && !isSmile },
                  itemCN
                )}
                {...attrs}>
                {renderItem(item, i)}
              </li>
            );
          })}
        </ul>
      </div>
      {isOverflown && controlsPlacement === CONTROLS_PLACEMENTS.bothSides && <NextNavigationWithHandlers {...navigationProps} />}
      {isOverflown && controlsPlacement === CONTROLS_PLACEMENTS.rightSide && <RsSlidableNavigationRightSide {...navigationProps} />}
    </div>
  );
}

RsSlidable.propTypes = {
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  contentAttrs: PropTypes.object,
  itemClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  itemAttrs: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  items: PropTypes.array,
  renderItem: PropTypes.func,
  visibleItemIndex: PropTypes.number,
  resized: PropTypes.number,
  centerItemsIfNavigationControlsNotVisible: PropTypes.bool,
  controlsPlacement: PropTypes.oneOf(Object.values(CONTROLS_PLACEMENTS)),
  isSmile: PropTypes.bool,
};

export default RsSlidable;
